import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User as AuthenticatedUser } from 'oidc-client-ts';
import { batch } from 'react-redux';
import { Hub, Project, SessionProps, User } from '@bimone/bimtrack-core';
import UserClient from '../../clients/user/UserClient';
import BimTrackConfiguration from '../../configuration/BimTrackConfiguration';
import { ApplicationStore } from '../types';
import executeWithRetry from '../../../common/utilities/executeWithRetry';
import getAuthentication from '../../../common/utilities/getAuthentication';

export interface UpdateSessionPayload {
  authenticatedUser?: AuthenticatedUser;
}

const defaultProps: SessionProps = {
  user: undefined,
  hub: undefined,
  project: undefined,
  bimTrackApiUrl: '',
};

const sessionSlice = createSlice({
  name: 'session',
  initialState: defaultProps,
  reducers: {
    updateUser: (state, action: PayloadAction<User | undefined>): void => {
      state.user = action.payload;
    },
    internalUpdateHub: (state, action: PayloadAction<Hub | undefined>): void => {
      state.hub = action.payload;
    },
    updateProject: (state, action: PayloadAction<Project | undefined>): void => {
      state.project = action.payload;
    },
    updateBimTrackApi: (state, action: PayloadAction<string>): void => {
      state.bimTrackApiUrl = action.payload;
    },
  },
});

export default sessionSlice.reducer;

export const { updateUser, updateProject, updateBimTrackApi } = sessionSlice.actions;

export const updateHub = createAsyncThunk<void, Hub | undefined>(
  'session/updateHub',
  (payload: Hub | undefined, thunkAPI) => {
    const newHub = payload;
    const { session } = thunkAPI.getState() as ApplicationStore;
    const { user, hub: previousHub, bimTrackApiUrl } = session;
    const { internalUpdateHub } = sessionSlice.actions;

    batch(async () => {
      thunkAPI.dispatch(internalUpdateHub(newHub));

      if (newHub === previousHub) {
        return;
      }

      const accessToken = getAuthentication()?.accessToken;

      if (!user || !newHub || !bimTrackApiUrl || !accessToken) {
        return;
      }

      try {
        const updatedUser = await executeWithRetry(() =>
          UserClient.fetchUserPreferencesByHub(user, newHub.hashId, bimTrackApiUrl, accessToken)
        );

        thunkAPI.dispatch(updateUser(updatedUser));
      } catch (error) {
        logUserPreferenceError(error);
      }
    });
  }
);

export const updateSession = createAsyncThunk<void, UpdateSessionPayload>(
  'session/updateSession',
  (payload: UpdateSessionPayload, thunkAPI): void => {
    batch(async () => {
      const bimTrackApiUrl = BimTrackConfiguration.getBimTrackApiBaseUrl();

      thunkAPI.dispatch(updateBimTrackApi(bimTrackApiUrl));

      const { authenticatedUser } = payload ?? {};

      if (!authenticatedUser) {
        thunkAPI.dispatch(updateUser(undefined));

        return;
      }

      let user = await new UserClient(authenticatedUser).fetchAuthenticatedUser();
      const { access_token: accessToken } = authenticatedUser;
      const {
        session: { hub },
      } = thunkAPI.getState() as ApplicationStore;

      if (hub?.hashId && accessToken) {
        try {
          user = await executeWithRetry(() =>
            UserClient.fetchUserPreferencesByHub(user, hub.hashId, bimTrackApiUrl, accessToken)
          );
        } catch (error) {
          logUserPreferenceError(error);
        }
      }

      thunkAPI.dispatch(updateUser(user));
      thunkAPI.dispatch(updateBimTrackApi(bimTrackApiUrl));
    });
  }
);

function logUserPreferenceError(error: unknown): void {
  console.error(
    `Could not retrieve the user preferences. User's avatar, language, date formats and time zone would ` +
      `reflect the browser or the previously fetched preferences`,
    error
  );
}
